import {
  MODAL_PAGINATION_PAGE_NAMESPACE, MODAL_PAGINATION_SIZE_NAMESPACE,
  PAGINATION_DEFAULT_PAGE,
  PAGINATION_DEFAULT_SIZE,
  PAGINATION_SIZE_OPTIONS
} from '../../../../core/pagination';
import { CUSTOMER_STATUS_MAP, CustomerStatus } from '../../../../enums/customerStatus';
import { ITableColumn } from '../../../../interfaces/tableProps';
import { theme } from '../../../../theme';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useQueryParamsState } from '../../../../hooks/useQueryParamsState';
import { useGetFreelancersList } from '../../../../queryHooks/freelancer/useGetFreelancersList';
import FreelancersTableWithSearch from '../../../freelancers/FreelancersTableWithSearch';
import { ISearchHeaderInputProps } from '../../../../interfaces/searchHeaderProps';
import { IProjectPosition, IProjectPositionStringSkills } from '../../../../interfaces/projectPosition';
import { Loader } from '../../Loader';
import { Typography } from '@mui/material';
import { TEXTS } from '../../../../core/texts';
import { IFreelancerSearchRequest, IFreelancerSearchResponse } from '../../../../interfaces/freelancerSearch';

const searchInputProps: ISearchHeaderInputProps[] = [
  { name: 'id', label: 'ID', sx: { width: 100 }, isNumber: true },
  { name: 'email', label: 'Email' },
  { name: 'firstName', label: 'First Name' },
  { name: 'lastName', label: 'Last Name' },
  { name: 'country', label: 'Country' },
  { name: 'town', label: 'Town' },
  { name: 'phone', label: 'Phone' },
];

interface SearchState extends Omit<IFreelancerSearchRequest, 'page' | 'size'> {
  [MODAL_PAGINATION_PAGE_NAMESPACE]: number;
  [MODAL_PAGINATION_SIZE_NAMESPACE]: number;
}

export const initialSearchState: SearchState = {
  [MODAL_PAGINATION_PAGE_NAMESPACE]: PAGINATION_DEFAULT_PAGE,
  [MODAL_PAGINATION_SIZE_NAMESPACE]: PAGINATION_DEFAULT_SIZE,
  id: '',
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  status: '' as CustomerStatus,
  country: '',
  town: '',
  fromDate: '',
  toDate: '',
};

const columns: ITableColumn<keyof IFreelancerSearchResponse>[] = [
  {
    name: 'id',
    label: 'ID',
    sx: { '&:hover': { cursor: 'pointer', textDecoration: 'underline', color: theme.palette.primary.dark } },
    withCopyIcon: true,
  },
  { name: 'firstName', label: 'First Name' },
  { name: 'lastName', label: 'Last Name' },
  { name: 'email', label: 'Email' },
  { name: 'roleName', label: 'Role'},
  { name: 'positionName', label: 'Position'},
  { name: 'country', label: 'Country' },
  { name: 'town', label: 'Town' },
  { name: 'status', label: 'Status', format: (val: CustomerStatus) => CUSTOMER_STATUS_MAP[val] },
  { name: 'phone', label: 'Phone' },
];

interface IProps {
  projectPosition: IProjectPosition | IProjectPositionStringSkills,
  selectedFreelancerId: null | number;
  setSelectedFreelancerId: Dispatch<SetStateAction<null | number>>
}

const ApplicantFreelancersTable = ({ projectPosition, setSelectedFreelancerId, selectedFreelancerId }: IProps) => {
  const [searchState, setSearchState] = useState<SearchState>(initialSearchState);
  const [totalCount, setTotalCount] = useState(0);

  const { queryParamState, isPrePopulated } = useQueryParamsState({
    state: searchState,
    setState: setSearchState,
    wildcards: ['email', 'firstName', 'lastName', 'phone', 'town', 'country'],
    parser: { size: (val: string) => PAGINATION_SIZE_OPTIONS.includes(+val) ? +val : PAGINATION_SIZE_OPTIONS[0] }
  });

  const parsedQueryParamState = useMemo(() => {
    const { [MODAL_PAGINATION_SIZE_NAMESPACE]: _, [MODAL_PAGINATION_SIZE_NAMESPACE]: __, ...rest } = queryParamState;
    return rest;
  }, [queryParamState]);

  const { data: freelancersList, isLoading } = useGetFreelancersList({
    queryParamState: {
      ...parsedQueryParamState,
      status: CustomerStatus.ACTIVE,
      // roleId: projectPosition.roleId,
      // positionId: projectPosition.positionId,
      page: queryParamState[MODAL_PAGINATION_PAGE_NAMESPACE],
      size: queryParamState[MODAL_PAGINATION_SIZE_NAMESPACE]
    },
    onSuccess: (data) => {
      setTotalCount(data.size * data.totalPages - (data.size - data.totalElements));
      setSelectedFreelancerId(null);
    }
  });

  const handleOnChangeCheckbox = useCallback((freelancer: IFreelancerSearchResponse) => {
    setSelectedFreelancerId(prev => prev !== freelancer.id ? freelancer.id : null);
  }, [setSelectedFreelancerId]);

  const isCheckedHandler = useCallback((f: IFreelancerSearchResponse) => f.id === selectedFreelancerId, [selectedFreelancerId]);

  return (
    <>
      <Typography variant={'h4'} sx={{ mb: 1 }}>
        {TEXTS.FREELANCERS_LIST}
      </Typography>
      {
        isLoading && <Loader width={50}/>
      }
      {
        freelancersList && isPrePopulated &&
        <FreelancersTableWithSearch
          checkbox={{
            checked: isCheckedHandler,
            onChange: handleOnChangeCheckbox
          }}
          searchInputProps={searchInputProps}
          columns={columns}
          tableData={{
            list: freelancersList.content,
            pagination: {
              size: searchState[MODAL_PAGINATION_SIZE_NAMESPACE],
              page: searchState[MODAL_PAGINATION_PAGE_NAMESPACE],
              setState: setSearchState,
              sizeOptions: PAGINATION_SIZE_OPTIONS,
              totalCount: totalCount
            }
          }}
          searchProps={{
            initialState: initialSearchState,
            params: searchState,
            setParams: setSearchState
          }}
          pageNamespace={MODAL_PAGINATION_PAGE_NAMESPACE}
          sizeNamespace={MODAL_PAGINATION_SIZE_NAMESPACE}
        />
      }
    </>
  );
};

export default ApplicantFreelancersTable;